import { memo, Suspense } from 'react';
import dayjs from 'dayjs';
import { Await, useLoaderData, useSearchParams } from 'react-router-dom';
import { Loader } from '@components';
import { Avatar, Paragraph } from '@quickmd-llc/design-system';
import { formatProviderNameWithTitle, isDateToday } from '@utils';
import { Availabilities, Provider } from '@types';
import Tag from '../Tag';
import { getTimeSlotByProviderId } from '../../utils';
import { AvailabilitiesLoader } from '@router/loaders/scheduleLoader';

interface Props {
  lastEndedVisitDateTime?: string;
  provider: Provider;
  isProviderFiltered?: boolean;
  soonest?: boolean;
  sameDate?: boolean;
}

function ProviderDetails({
  lastEndedVisitDateTime,
  provider,
  isProviderFiltered,
  soonest,
  sameDate,
}: Props): JSX.Element | null {
  const [searchParams] = useSearchParams();
  const { availabilities: availabilitiesPromise } =
    useLoaderData() as AvailabilitiesLoader;

  const showTag = (soonest || lastEndedVisitDateTime) && !isProviderFiltered;
  const tagText = showTag
    ? lastEndedVisitDateTime
      ? `Last visit on ${dayjs(lastEndedVisitDateTime).format('MM/DD')}`
      : 'Soonest'
    : '';

  const { firstName, id, imageUrl, lastName } = provider;
  const date = searchParams.get('date') || '';

  const providerName = `${firstName} ${lastName}`;
  const providerNameWithTitle = formatProviderNameWithTitle(provider);

  return (
    <Suspense fallback={<Loader />}>
      <Await resolve={availabilitiesPromise}>
        {({ availabilities }: Availabilities) => {
          const { times } = getTimeSlotByProviderId(availabilities, id, date);

          const availabilitiesText =
            sameDate || isProviderFiltered
              ? getAvailabilitiesText(times, date)
              : 'No available visits';

          return (
            <div className="flex items-center gap-3">
              <Avatar name={providerName} src={imageUrl} />
              <div>
                <Paragraph className="break-all font-semibold text-text-dark">
                  {providerNameWithTitle}
                </Paragraph>
                <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                  {showTag && <Tag text={tagText} />}
                  <Paragraph size="xs" className="text-neutral-70">
                    {availabilitiesText}
                  </Paragraph>
                </div>
              </div>
            </div>
          );
        }}
      </Await>
    </Suspense>
  );
}

function getAvailabilitiesText(times: string[], date: string) {
  const d = dayjs(date);
  let dateStr: string;

  if (isDateToday(d)) {
    dateStr = 'today';
  } else {
    dateStr = d.format('[on] MMM D');
  }

  return `${times.length} availabilit${times.length === 1 ? 'y' : 'ies'} ${dateStr}`;
}

export default memo(ProviderDetails);
